import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

type AuthShellProps = {
    eyebrow: string;
    title: string;
    description: string;
    children: React.ReactNode;
};

const authHighlights = [
    {
        icon: <AutoStoriesOutlinedIcon sx={{ fontSize: 18 }} />,
        label: "Organized knowledge",
        description: "Searchable collections and structured document processing.",
    },
    {
        icon: <ShieldOutlinedIcon sx={{ fontSize: 18 }} />,
        label: "Secure access",
        description: "Protected authentication flow for your organization.",
    },
    {
        icon: <CloudUploadOutlinedIcon sx={{ fontSize: 18 }} />,
        label: "Modern workflows",
        description: "Upload, process, and explore your content in one place.",
    },
];

export default function AuthShell({ eyebrow, title, description, children }: AuthShellProps) {
    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: { xs: 2, md: 4 },
                py: { xs: 3, md: 5 },
                background: "linear-gradient(180deg, #f8f4ec 0%, #f2ebe0 100%)",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 1180,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(320px, 0.95fr) minmax(420px, 0.8fr)" },
                    borderRadius: "32px",
                    overflow: "hidden",
                    border: "1px solid rgba(138, 106, 74, 0.14)",
                    boxShadow: "0 30px 80px rgba(73, 57, 37, 0.12)",
                    backgroundColor: "#fcfaf6",
                }}
            >
                <Box
                    sx={{
                        display: { xs: "none", md: "flex" },
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: 4,
                        minHeight: 720,
                        p: 5,
                        color: "#f7f1e8",
                        background: "radial-gradient(circle at top left, rgba(230, 210, 179, 0.26), transparent 36%), linear-gradient(160deg, #30261c 0%, #4e3b2b 52%, #7c6143 100%)",
                    }}
                >
                    <Stack spacing={2}>
                        <Chip
                            label={eyebrow}
                            sx={{
                                alignSelf: "flex-start",
                                bgcolor: "rgba(255,255,255,0.08)",
                                color: "#f7f1e8",
                                borderRadius: "999px",
                                border: "1px solid rgba(255,255,255,0.14)",
                                fontWeight: 600,
                                backdropFilter: "blur(6px)",
                            }}
                        />
                        <Typography
                            sx={{
                                fontFamily: "'Instrument Serif', serif",
                                fontSize: "clamp(2.6rem, 4.2vw, 4rem)",
                                lineHeight: 0.95,
                                letterSpacing: "-0.03em",
                            }}
                        >
                            MindVault
                        </Typography>
                        <Typography sx={{ maxWidth: 360, fontSize: 16, lineHeight: 1.8, color: "rgba(247, 241, 232, 0.78)" }}>
                            Keep your document workflows, internal knowledge, and team access in one calmer, easier place.
                        </Typography>
                    </Stack>

                    <Stack spacing={2}>
                        {authHighlights.map((item) => (
                            <Box
                                key={item.label}
                                sx={{
                                    display: "flex",
                                    gap: 2,
                                    alignItems: "flex-start",
                                    p: 2.25,
                                    borderRadius: "20px",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    bgcolor: "rgba(255,255,255,0.06)",
                                    backdropFilter: "blur(10px)",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: "12px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "rgba(255,255,255,0.12)",
                                        color: "#f3e6d3",
                                        flexShrink: 0,
                                    }}
                                >
                                    {item.icon}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{item.label}</Typography>
                                    <Typography sx={{ mt: 0.5, fontSize: 13.5, lineHeight: 1.7, color: "rgba(247, 241, 232, 0.7)" }}>
                                        {item.description}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: { xs: 2, sm: 3, md: 5 },
                        background:
                            "radial-gradient(circle at top right, rgba(224, 205, 176, 0.35), transparent 32%), linear-gradient(180deg, #fcfaf6 0%, #f7f2ea 100%)",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 440,
                            p: { xs: 3, sm: 4 },
                            borderRadius: "28px",
                            border: "1px solid rgba(138, 106, 74, 0.12)",
                            bgcolor: "rgba(255, 252, 247, 0.88)",
                            boxShadow: "0 18px 48px rgba(73, 57, 37, 0.08)",
                            backdropFilter: "blur(10px)",
                        }}
                    >
                        <Typography sx={{ color: "#8a6a4a", fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                            {eyebrow}
                        </Typography>
                        <Typography
                            variant="h3"
                            sx={{
                                mt: 1.5,
                                color: "#1f1a15",
                                fontFamily: "'Instrument Serif', serif",
                                fontSize: { xs: "2.4rem", sm: "2.9rem" },
                                lineHeight: 0.98,
                                letterSpacing: "-0.03em",
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography sx={{ mt: 1.5, color: "#6f675d", fontSize: 14.5, lineHeight: 1.8 }}>
                            {description}
                        </Typography>
                        <Box sx={{ mt: 4 }}>
                            {children}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
